import React, { useState } from 'react';
import axios from 'axios';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const DamageReportModal = ({ isOpen, onClose, reservation }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    status: 'reported',
    visibility: true,
    image: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { bookingid, orderid } = reservation || {};

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData({ ...formData, image: files[0] });
    } else if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // First, submit the damage report
    const damagePayload = new FormData();
    damagePayload.append('bookingid', bookingid);
    damagePayload.append('orderid', orderid || '');
    damagePayload.append('title', formData.title);
    damagePayload.append('description', formData.description);
    damagePayload.append('amount', formData.amount);
    damagePayload.append('status', formData.status);
    damagePayload.append('visibility', formData.visibility);
    if (formData.image) {
      damagePayload.append('image', formData.image);
    }

    try {
      // Step 1: Submit damage report
      const damageResponse = await axios.post(`${BQ_BASE_URL}/bq/api/damages/`, damagePayload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Damage report submitted:', damageResponse.data);

      // Extract the damage_id from the response - ensure it's properly formatted
      const damageId = damageResponse.data?.id;

      if (!damageId || typeof damageId !== 'string') {
        throw new Error('Invalid damage ID received from server');
      }

      // Step 2: Create damage billing
      const billingResponse = await axios.post(
        `${BQ_BASE_URL}/bq/api/create_damage_billing`,
        {
          bookingid: bookingid,
          damage_id: damageId,
        },
        {
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          params: {
            bookingid: bookingid,
            damage_id: damageId,
          },
        }
      );

      console.log('Billing created:', billingResponse.data);

      // Show success toast
      toast.success(
        <div>
          <p className="text-sm">Damage reported successfully!</p>
          <p className="text-sm">Billing has been created for the damage charges.</p>
        </div>,
        {
          autoClose: 5000,
        }
      );

      // Close modal after successful submission
      setTimeout(() => {
        onClose();
        // Reset form
        setFormData({
          title: '',
          description: '',
          amount: '',
          status: 'reported',
          visibility: true,
          image: null,
        });
      }, 2000);
    } catch (error) {
      console.error('Error in damage reporting process:', error);
      let errorMessage = 'Error in damage reporting process.';

      if (error.response) {
        if (error.response.data) {
          errorMessage =
            error.response.data.message ||
            error.response.data.error ||
            `Server responded with status ${error.response.status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show error toast
      toast.error(
        <div>
          <p className="font-semibold">Failed to report damage</p>
          <p className="text-sm">{errorMessage}</p>
        </div>,
        {
          autoClose: 5000,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-lg relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
          aria-label="Close modal"
          disabled={isSubmitting}
        >
          <FaTimes size={20} />
        </button>

        <h2 className="text-lg font-semibold mb-1 text-black"> Damage Report</h2>
        <p className="text-sm text-gray-600 mb-3">
          Record any damages caused by the guest during their stay. These charges will be added to
          the guest's final bill.
        </p>

        <div className="mb-4 p-3 bg-gray-100 rounded">
          <p className="font-medium">This is for reservation number: {orderid}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              name="title"
              placeholder="E.g., Broken TV"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              name="description"
              placeholder="Describe the damage in detail..."
              value={formData.description}
              onChange={handleChange}
              required
              rows={2}
              className="w-full border px-3 py-2 rounded"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (in ₹) *</label>
            <input
              type="number"
              name="amount"
              placeholder="eg. 300"
              value={formData.amount}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visible to Guest
              </label>
              <p className="text-xs text-gray-500">
                {formData.visibility
                  ? 'Guest will see this damage report'
                  : 'Only staff will see this damage report'}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="visibility"
                checked={formData.visibility}
                onChange={handleChange}
                className="sr-only peer"
                disabled={isSubmitting}
              />
              <div className="relative w-11 h-6 bg-gray-200 rounded-full peer-focus:outline-none peer peer-checked:bg-black after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Damage Photo (Optional)
            </label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-950 hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Submit Damage Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DamageReportModal;

// import React, { useState } from "react";
// import axios from "axios";
// import { FaTimes } from "react-icons/fa";
// import { toast } from "react-toastify";

// const DamageReportModal = ({ isOpen, onClose, reservation }) => {
//   const [formData, setFormData] = useState({
//     title: "",
//     description: "",
//     amount: "",
//     status: "reported",
//     visibility: true,
//     image: null,
//   });
//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const { bookingid, orderid } = reservation || {};

//   const handleChange = (e) => {
//     const { name, value, type, checked, files } = e.target;
//     if (type === "file") {
//       setFormData({ ...formData, image: files[0] });
//     } else if (type === "checkbox") {
//       setFormData({ ...formData, [name]: checked });
//     } else {
//       setFormData({ ...formData, [name]: value });
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setIsSubmitting(true);

//     // First, submit the damage report
//     const damagePayload = new FormData();
//     damagePayload.append("bookingid", bookingid);
//     damagePayload.append("orderid", orderid || "");
//     damagePayload.append("title", formData.title);
//     damagePayload.append("description", formData.description);
//     damagePayload.append("amount", formData.amount);
//     damagePayload.append("status", formData.status);
//     damagePayload.append("visibility", formData.visibility);
//     if (formData.image) {
//       damagePayload.append("image", formData.image);
//     }

//     try {
//       // Step 1: Submit damage report
//       const damageResponse = await axios.post(
//         "http://localhost:9000/bq/api/damages/",
//         damagePayload,
//         {
//           headers: {
//             "Content-Type": "multipart/form-data",
//           },
//         }
//       );

//       console.log("Damage report submitted:", damageResponse.data);

//       // Extract the damage_id from the response - ensure it's properly formatted
//       const damageId = damageResponse.data?.id;

//       if (!damageId || typeof damageId !== "string") {
//         throw new Error("Invalid damage ID received from server");
//       }

//       // Step 2: Create damage billing
//       const billingResponse = await axios.post(
//         `http://localhost:9000/bq/api/create_damage_billing`,
//         {
//           bookingid: bookingid,
//           damage_id: damageId
//         },
//         {
//           headers: {
//             "accept": "application/json",
//             "Content-Type": "application/json",
//           },
//           params: {
//             bookingid: bookingid,
//             damage_id: damageId
//           }
//         }
//       );

//       console.log("Billing created:", billingResponse.data);

//       // Show success toast
//       toast.success(
//         <div>
//           <p className="text-sm">Damage reported successfully!</p>
//           <p className="text-sm">Billing has been created for the damage charges.</p>
//         </div>,
//         {
//           autoClose: 5000,
//         }
//       );

//       // Close modal after successful submission
//       setTimeout(() => {
//         onClose();
//         // Reset form
//         setFormData({
//           title: "",
//           description: "",
//           amount: "",
//           status: "reported",
//           visibility: true,
//           image: null,
//         });
//       }, 2000);

//     } catch (error) {
//       console.error("Error in damage reporting process:", error);
//       let errorMessage = "Error in damage reporting process.";

//       if (error.response) {
//         if (error.response.data) {
//           errorMessage = error.response.data.message ||
//             error.response.data.error ||
//             `Server responded with status ${error.response.status}`;
//         }
//       } else if (error.message) {
//         errorMessage = error.message;
//       }

//       // Show error toast
//       toast.error(
//         <div>
//           <p className="font-semibold">Failed to report damage</p>
//           <p className="text-sm">{errorMessage}</p>
//         </div>,
//         {
//           autoClose: 5000,
//         }
//       );
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   if (!isOpen) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
//       <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-lg relative">
//         <button
//           onClick={onClose}
//           className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
//           aria-label="Close modal"
//           disabled={isSubmitting}
//         >
//           <FaTimes size={20} />
//         </button>

//         <h2 className="text-lg font-semibold mb-1 text-black"> Damage Report</h2>
//         <p className="text-sm text-gray-600 mb-3">
//           Record any damages caused by the guest during their stay. These charges will be added to the guest's final bill.
//         </p>

//         <div className="mb-4 p-3 bg-gray-100 rounded">
//           <p className="font-medium">This is for reservation number: {orderid}</p>

//         </div>

//         <form onSubmit={handleSubmit} className="space-y-2">
//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Title *
//             </label>
//             <input
//               type="text"
//               name="title"
//               placeholder="E.g., Broken TV"
//               value={formData.title}
//               onChange={handleChange}
//               required
//               className="w-full border px-3 py-2 rounded"
//               disabled={isSubmitting}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Description *
//             </label>
//             <textarea
//               name="description"
//               placeholder="Describe the damage in detail..."
//               value={formData.description}
//               onChange={handleChange}
//               required
//               rows={2}
//               className="w-full border px-3 py-2 rounded"
//               disabled={isSubmitting}
//             />
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Amount (in ₹) *
//             </label>
//             <input
//               type="number"
//               name="amount"
//               placeholder="eg. 300"
//               value={formData.amount}
//               onChange={handleChange}
//               required
//               className="w-full border px-3 py-2 rounded"
//               disabled={isSubmitting}
//             />
//           </div>

//           <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
//             <div>
//               <label className="block text-sm font-medium text-gray-700 mb-1">
//                 Visible to Guest
//               </label>
//               <p className="text-xs text-gray-500">
//                 {formData.visibility
//                   ? "Guest will see this damage report"
//                   : "Only staff will see this damage report"}
//               </p>
//             </div>
//             <label className="relative inline-flex items-center cursor-pointer">
//               <input
//                 type="checkbox"
//                 name="visibility"
//                 checked={formData.visibility}
//                 onChange={handleChange}
//                 className="sr-only peer"
//                 disabled={isSubmitting}
//               />
//               <div className="relative w-11 h-6 bg-gray-200 rounded-full peer-focus:outline-none peer peer-checked:bg-black after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
//             </label>
//           </div>

//           <div>
//             <label className="block text-sm font-medium text-gray-700 mb-1">
//               Damage Photo (Optional)
//             </label>
//             <input
//               type="file"
//               name="image"
//               accept="image/*"
//               onChange={handleChange}
//               className="block w-full text-sm text-gray-500
//                 file:mr-4 file:py-2 file:px-4
//                 file:rounded-md file:border-0
//                 file:text-sm file:font-semibold
//                 file:bg-blue-50 file:text-blue-700
//                 hover:file:bg-blue-100"
//               disabled={isSubmitting}
//             />
//           </div>

//           <div className="flex justify-end space-x-2 pt-4">
//             <button
//               type="button"
//               onClick={onClose}
//               className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
//               disabled={isSubmitting}
//             >
//               Cancel
//             </button>
//             <button
//               type="submit"
//               className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-950 hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
//               disabled={isSubmitting}
//             >
//               {isSubmitting ? "Processing..." : "Submit Damage Report"}
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default DamageReportModal;
